// src/components/SearchWithAiButton/SearchWithAiButton.jsx

import React from "react";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Button from "@mui/material/Button";
import NobiEventTracker from "../../utils/NobiEventTracker";
import Box from "@mui/material/Box";
import Events from "../../utils/Events";
import {EntryPoint} from "../Chatbot/EntryPoint";

import styles from "./SearchWithAiButton.module.css";

/**
 * SearchWithAiButton component, a simple button that calls up our drawer
 *
 * @param merchantId The merchant ID
 * @param brandTheme A dictionary of brand theme attributes
 * @param onClick The function to call when the button is clicked
 * @param isSubmitForForm A boolean to determine if the button is a submit button for a form
 * @param buttonLabel The label for the button
 * @param isTextButton A boolean to determine if the button should be styled as a text button
 * @param additionalClassNames Additional class names to apply to the button (string or array)
 * @param iconUrl The URL of the icon to display in the button
 * @constructor
 */
const SearchWithAiButton = ({
    merchantId,
    brandTheme,
    onClick,
    isSubmitForForm,
    buttonLabel,
    entryPoint = EntryPoint.SearchBar,
    isTextButton = false,
    additionalClassNames = null,
    iconUrl = null}) => {

    let appliedStyle = {
        backgroundColor: isTextButton ? null : brandTheme?.general?.buttons?.backgroundColor,
        color: isTextButton
            ? "inherit"
            : brandTheme?.general?.buttons?.textColor
                ? brandTheme.general.buttons.textColor + " !important"
                : "inherit",
        fontSize: isSubmitForForm ? "14px" : null,
        '&:hover': {
            backgroundColor: isTextButton ? "transparent" : brandTheme?.general?.buttons?.hoverBackgroundColor || brandTheme?.general?.buttons?.backgroundColor,
            border: isTextButton ? null : brandTheme?.general?.buttons?.hoverBorder || "none",
            color: isTextButton ? null : brandTheme?.general?.buttons?.hoverTextColor
                ? brandTheme.general.buttons.hoverTextColor + " !important"
                : brandTheme?.general?.buttons?.textColor
                    ? brandTheme.general.buttons.textColor + " !important"
                    : "inherit",
        },
        fontFamily: brandTheme?.general?.buttons?.fontFamily || "inherit",
    }

    function renderCustomIconAsSvg() {
        return <object
            type="image/svg+xml"
            data={iconUrl}
            aria-label="AI Icon"
            style={{
                width: 12,
                height: 12
            }}
        />
    }

    function renderCustomIconAsImg() {
        return (
            <Box
                component="img"
                src={iconUrl}
                alt="AI Icon"
                sx={{
                    width: 12,
                    height: 12,
                    filter: "invert(100%)"
                }}
            />
        );
    }

    function renderCustomIcon() {
        if (iconUrl?.toLowerCase().includes(".svg")) {
            return renderCustomIconAsSvg();
        }
        else {
            return renderCustomIconAsImg();
        }
    }

    function renderDefaultIcon() {
        return (
            <AutoAwesomeIcon
                sx={{
                    color: isTextButton
                        ? "inherit"
                        : (brandTheme?.general?.buttons?.textColor + " !important"),
                    transition: "color 0.2s",
                    "& path": {
                        fill: isTextButton
                            ? "inherit"
                            : (brandTheme?.general?.buttons?.textColor + " !important")
                    }
                }}
            />
        );
    }

    function renderStartIcon() {
        if (iconUrl) {
            return renderCustomIcon();
        }
        return renderDefaultIcon();
    }

    return <Button
        disableElevation
        component={isTextButton ? "a" : undefined}
        href={isTextButton ? "#" : undefined}
        disableRipple={isTextButton}
        type={isTextButton ? undefined : isSubmitForForm ? "submit" : "button"}
        variant={isTextButton ? "text" : "contained"}
        onClick={(e) => {
            if (isTextButton) {
                e.preventDefault(); // prevent the “#” jump
            }

            NobiEventTracker.trackEvent(
                merchantId,
                null,
                entryPoint,
                Events.ClickSearchWithAiButton);

            if (onClick) {
                onClick();
            }
        }}
        aria-label="Search With AI"
        startIcon={renderStartIcon()}
        className={`${styles.aiButton} ${isTextButton ? styles.textButton : ""} ${Array.isArray(additionalClassNames) ? additionalClassNames.join(' ') : additionalClassNames || ''}`}
        sx={appliedStyle}
    >
        {buttonLabel || "Search With AI"}
    </Button>
};

export default SearchWithAiButton;